import { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { LOCALE } from "@constants";
import { COUNTRY_TO_LANGUAGE, TIMEZONE_TO_LANGUAGE } from "src/constants";

// --- Helpers ---
const detectLanguageByIP = async () => {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 2000);

    const response = await fetch("https://ipwho.is/", {
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) throw new Error(`API Status: ${response.status}`);

    const data = await response.json();

    if (data.success === false) {
      throw new Error(`API Error: ${data.message}`);
    }

    const countryCode = data.country_code;
    const lang = COUNTRY_TO_LANGUAGE[countryCode];

    if (lang) {
      return lang;
    } else {
      console.log(`⚠️ IP Detection: Country ${countryCode} not in map.`);
    }
  } catch (error) {
    console.warn(`❌ IP Detection Failed: ${error.message}`);
  }
  return null;
};

const detectLanguageByTimezone = () => {
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const lang = TIMEZONE_TO_LANGUAGE[timezone];

    if (lang) {
      return lang;
    } else {
      console.log(`⚠️ Timezone ${timezone} not in map.`);
    }
  } catch (e) {
    console.log("❌ Timezone detection error", e);
  }
  return null;
};

const detectLanguageByBrowser = (supported) => {
  try {
    const rawLang = navigator.language || navigator.userLanguage;
    const lang = rawLang.split("-")[0];

    if (supported.includes(lang)) {
      return lang;
    } else {
      console.log(`⚠️ Browser Lang '${lang}' not supported.`);
    }
  } catch (e) {
    console.log("❌ Browser detection error", e);
  }
  return null;
};

// --- MAIN HOOK ---
export const useLanguageDetection = (
  supportedLanguages = ["en", "ua", "ru", "es"],
) => {
  const dispatch = useDispatch();
  const { locale } = useSelector((state) => state.localization);
  const [isDetecting, setIsDetecting] = useState(false);

  const hasCheckedRef = useRef(false);

  useEffect(() => {
    if (hasCheckedRef.current) return;

    // Stop if language is already valid in Redux
    if (locale && supportedLanguages.includes(locale)) {
      hasCheckedRef.current = true;
      return;
    }

    const runDetection = async () => {
      setIsDetecting(true);
      let detected = null;

      detected = await detectLanguageByIP();
      if (!detected) detected = detectLanguageByTimezone();
      if (!detected) detected = detectLanguageByBrowser(supportedLanguages);

      if (!detected || !supportedLanguages.includes(detected)) {
        detected = "en";
      }

      if (detected !== locale) {
        dispatch({
          type: LOCALE.CHANGE_LOCALE,
          payload: detected,
        });
      }

      hasCheckedRef.current = true;
      setIsDetecting(false);
    };

    runDetection();
  }, [dispatch, locale, supportedLanguages]);

  return { isDetecting };
};
